import { useRef, useState } from 'react';
import { Box, Popover, Typography } from '@mui/material';
import pendingRequestsStyle from '../../Styles/ComponentStyles/PendingRequests/PendingRequests.module.scss';

const MAX_VISIBLE_LICENSES = 1;

export function parseLicensesHold(licenses: unknown): string[] {
    if (!Array.isArray(licenses)) return [];
    return licenses.map((l) => String(l).trim()).filter(Boolean);
}

function LicenseChip({ label }: { label: string }) {
    return <span className={pendingRequestsStyle.licenseChip}>{label}</span>;
}

interface LicensesHoldCellProps {
    licenses: string[];
}

export function LicensesHoldCell({ licenses }: LicensesHoldCellProps) {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const visible = licenses.slice(0, MAX_VISIBLE_LICENSES);
    const overflow = licenses.slice(MAX_VISIBLE_LICENSES);
    const hasOverflow = overflow.length > 0;
    const open = Boolean(anchorEl) && hasOverflow;

    const clearCloseTimer = () => {
        if (closeTimerRef.current) {
            clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
    };

    const scheduleClose = () => {
        clearCloseTimer();
        closeTimerRef.current = setTimeout(() => setAnchorEl(null), 150);
    };

    const handleMoreEnter = (event: React.MouseEvent<HTMLElement>) => {
        clearCloseTimer();
        setAnchorEl(event.currentTarget);
    };

    if (!licenses.length) {
        return <Box className={pendingRequestsStyle.licensesHoldCell}>—</Box>;
    }

    return (
        <Box className={pendingRequestsStyle.licensesHoldCell}>
            {visible.map((license, index) => (
                <LicenseChip key={`${license}-${index}`} label={license} />
            ))}

            {hasOverflow && (
                <>
                    <span
                        className={pendingRequestsStyle.licenseChipMore}
                        onMouseEnter={handleMoreEnter}
                        onMouseLeave={scheduleClose}
                        role="button"
                        tabIndex={0}
                        aria-label={`${overflow.length} more license${overflow.length === 1 ? '' : 's'}`}
                    >
                        +{overflow.length}
                    </span>

                    <Popover
                        open={open}
                        anchorEl={anchorEl}
                        onClose={() => setAnchorEl(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                        disableRestoreFocus
                        sx={{ pointerEvents: 'none' }}
                        PaperProps={{
                            className: pendingRequestsStyle.licensesHoldPopoverPaper,
                            onMouseEnter: clearCloseTimer,
                            onMouseLeave: scheduleClose,
                            sx: { pointerEvents: 'auto' },
                        }}
                    >
                        <Box className={pendingRequestsStyle.licensesHoldPopoverContent}>
                            <Typography className={pendingRequestsStyle.licensesHoldPopoverTitle}>
                                Licenses Held
                            </Typography>
                            <Box className={pendingRequestsStyle.licensesHoldPopoverChips}>
                                {overflow.map((license, index) => (
                                    <LicenseChip key={`overflow-${license}-${index}`} label={license} />
                                ))}
                            </Box>
                        </Box>
                    </Popover>
                </>
            )}
        </Box>
    );
}
